// Invoice export downloads (Epic 4). The PDF + payments-CSV are authenticated binary reads: the access
// token is bearer-only (in memory, not a cookie), so a plain <a href> can't carry it — every call
// attaches the bearer here and triggers the download through an authenticated blob fetch → object URL,
// exactly like the animal media tile.
import { getAccessToken } from "../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** An HTTP failure carrying the status code so callers can branch (e.g. 404). */
export class FactuurExportError extends Error {
  readonly statusCode: number;
  constructor(statusCode: number, message?: string) {
    super(message ?? `HTTP ${statusCode}`);
    this.statusCode = statusCode;
    this.name = "FactuurExportError";
  }
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/** Fetch an authenticated blob and trigger a browser download via a temporary object-URL anchor. */
async function downloadBlob(path: string, fileName: string): Promise<void> {
  const res = await fetch(`${API_URL}/${path}`, { headers: authHeaders() });
  if (!res.ok) throw new FactuurExportError(res.status, res.statusText);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Download the invoice as a PDF (attachment factuur-<nummer>.pdf). */
export function downloadFactuurPdf(id: string, nummer: string): Promise<void> {
  return downloadBlob(`facturen/${id}/pdf`, `factuur-${nummer}.pdf`);
}

/** Download the invoice's payments as a CSV. */
export function downloadBetalingenCsv(id: string, nummer: string): Promise<void> {
  return downloadBlob(`facturen/${id}/betalingen.csv`, `factuur-${nummer}-betalingen.csv`);
}
